/**
 * Grant Report Obligations API client.
 *
 * Wraps /api/grant-obligations — the grant report *calendar*: per-grant
 * report due dates, recurrence, submitter and status. Recurring obligations
 * may link to a scheduled_reports row that auto-generates the report file.
 *
 * Backend: app/routers/grant_obligations.py. All calls go through authFetch
 * (Authorization + X-CBO-ID).
 */

import { authFetchJson } from '@/lib/authFetch';

export type GrantRecurrence =
  | 'one_time'
  | 'monthly'
  | 'quarterly'
  | 'annual'
  | 'monthly_within_days_of_month_end';

export type GrantObligationStatus = 'pending' | 'submitted' | 'overdue' | 'waived';

export interface GrantObligation {
  id: number;
  cbo_id: number;
  program_id?: number | null;
  grant_name: string;
  funder?: string | null;
  report_type?: string | null;
  recurrence: GrantRecurrence;
  recurrence_config: Record<string, any>;
  next_due_date?: string | null; // ISO date
  submitter_user_id?: number | null;
  submitter_name?: string | null;
  status: GrantObligationStatus;
  submitted_date?: string | null;
  scheduled_report_id?: number | null;
  notes?: string | null;
  is_overdue: boolean;
  created_at: string;
  updated_at: string;
  // Grant financials (Track B): award/budget + budget-vs-actual. spent_to_date
  // and budget_remaining are computed by the backend from the funds-flow ledger.
  award_amount?: number | null;
  total_budget?: number | null;
  currency?: string | null;
  budget_start_date?: string | null;
  budget_end_date?: string | null;
  fund_restriction?: 'restricted' | 'unrestricted' | null;
  spent_to_date?: number | null;
  budget_remaining?: number | null;
}

export interface GrantFinancialsUpdate {
  award_amount?: number | null;
  total_budget?: number | null;
  currency?: string | null;
  budget_start_date?: string | null;
  budget_end_date?: string | null;
  fund_restriction?: 'restricted' | 'unrestricted' | null;
}

export interface GrantObligationCreate {
  grant_name: string;
  funder?: string;
  report_type?: string;
  recurrence?: GrantRecurrence;
  recurrence_config?: Record<string, any>;
  next_due_date?: string;
  program_id?: number;
  submitter_name?: string;
  scheduled_report_id?: number;
  notes?: string;
  award_amount?: number;
  total_budget?: number;
  currency?: string;
  fund_restriction?: 'restricted' | 'unrestricted';
}

function buildQuery(params: Record<string, any> = {}): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') usp.set(k, String(v));
  }
  const s = usp.toString();
  return s ? `?${s}` : '';
}

export async function listGrantObligations(params: { status?: string; program_id?: number } = {}): Promise<GrantObligation[]> {
  return authFetchJson<GrantObligation[]>(`/api/grant-obligations${buildQuery(params)}`);
}

export async function listUpcomingObligations(withinDays = 30): Promise<GrantObligation[]> {
  return authFetchJson<GrantObligation[]>(`/api/grant-obligations/upcoming${buildQuery({ within_days: withinDays })}`);
}

export async function createGrantObligation(payload: GrantObligationCreate): Promise<GrantObligation> {
  return authFetchJson<GrantObligation>('/api/grant-obligations', { method: 'POST', jsonBody: payload });
}

export async function submitGrantObligation(id: number, submittedDate?: string): Promise<GrantObligation> {
  return authFetchJson<GrantObligation>(`/api/grant-obligations/${id}/submit`, {
    method: 'PATCH',
    jsonBody: { submitted_date: submittedDate },
  });
}

export async function updateGrantObligation(
  id: number,
  payload: GrantFinancialsUpdate & Record<string, any>,
): Promise<GrantObligation> {
  return authFetchJson<GrantObligation>(`/api/grant-obligations/${id}`, { method: 'PATCH', jsonBody: payload });
}

export async function deleteGrantObligation(id: number): Promise<void> {
  await authFetchJson<void>(`/api/grant-obligations/${id}`, { method: 'DELETE' });
}
